import cx from "classnames";
import { ForwardedRef, forwardRef, MouseEvent, ReactNode } from "react";
import { Button } from "react-aria-components";

import { Cross } from "components/icons/generated";
import { IconComponent } from "types/Icon";
import { getTagIconAndRenderValue } from "utils/tags";

import ButtonIcon from "../ButtonIcon";
import Icon from "../Icon";
import TextEllipsis from "../TextEllipsis";
import styles from "./styles.module.css";

export type TagProps = {
  tag: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  onRemove?: () => void;
  icon?: IconComponent;
  fullWidth?: boolean;
  withNewButton?: boolean;
  withAutoAttach?: boolean;
};

const Tag = forwardRef(function Tag(
  {
    tag,
    onClick,
    className,
    onRemove,
    icon,
    fullWidth = false,
    withNewButton = false,
    withAutoAttach = false,
    ...restProps
  }: TagProps,
  ref: React.ForwardedRef<HTMLButtonElement | HTMLDivElement>
) {
  const { isTextTag, tagIcon, tagValue } = getTagIconAndRenderValue(tag, icon, withAutoAttach);

  const isClickable = !!onClick && !onRemove;

  const fullClassName = cx(
    styles.tag,
    {
      [styles.action]: !!onClick || withNewButton,
      [styles.withRemoveIcon]: !!onRemove,
      [styles.fullWidth]: fullWidth,
    },
    className
  );

  const content = (
    <>
      {tagIcon && <Icon src={tagIcon} noShrink />}
      {isTextTag && (
        <TextEllipsis tooltip={tag} tooltipWidthMode="maxWidthSm">
          {(props) => <span {...props}>{tagValue}</span>}
        </TextEllipsis>
      )}
      {!isTextTag && tag}

      {onRemove && (
        // Don't inherit Button behavior/context from an ancestor ComboBox (e.g. its trigger
        // button's shared ref and open-on-press handling) when a Tag is rendered as a chip
        // inside one — see ComboBoxClearButton for the same fix.
        <ButtonIcon icon={Cross} onPress={onRemove} variant="ghost" slot={null}>
          Delete
        </ButtonIcon>
      )}
    </>
  );

  if (withNewButton) {
    return (
      <Button {...restProps} type="button" className={fullClassName}>
        {content}
      </Button>
    );
  }

  if (isClickable) {
    return (
      <button
        ref={ref as ForwardedRef<HTMLButtonElement>}
        onClick={onClick}
        type="button"
        className={fullClassName}
        {...restProps}
      >
        {content}
      </button>
    );
  }

  return (
    <div ref={ref as ForwardedRef<HTMLDivElement>} className={fullClassName} {...restProps}>
      {content}
    </div>
  );
});

Tag.displayName = "DS.Tag";

export default Tag;
